import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import Logo from "./Logo";
import { subscribeNewsletter } from "../lib/api";

const cols = [
  { title: "Company", links: [
    { to: "/about", label: "About" }, { to: "/work", label: "Our Work" },
    { to: "/events", label: "Events" }, { to: "/news", label: "News" },
  ]},
  { title: "Get Involved", links: [
    { to: "/hire", label: "NEAD Hire" }, { to: "/get-involved", label: "Volunteer" },
    { to: "/donate", label: "Donate" }, { to: "/contact", label: "Contact" },
  ]},
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await subscribeNewsletter(email);
      toast.success("You're on the list. Thank you.");
      setEmail("");
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setBusy(false); }
  };

  return (
    <footer className="bg-nead-ink text-white" data-testid="footer">
      <div className="container-x pt-20 pb-10">
        <div className="rounded-[1.75rem] grad-pink-cyan p-8 md:p-12 mb-16">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="font-display font-bold text-3xl md:text-4xl tracking-tight text-white">Stay in the know</h3>
              <p className="mt-3 text-white/85">Occasional updates on our work, events and opportunities.</p>
            </div>
            <form onSubmit={subscribe} className="flex items-center gap-3 rounded-full bg-white p-1.5 pl-6">
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@organisation.org" aria-label="Email address" data-testid="newsletter-input" className="flex-1 bg-transparent text-sm text-nead-navy placeholder:text-nead-navy/40 outline-none py-2.5" />
              <button type="submit" disabled={busy} aria-label="Subscribe" data-testid="newsletter-submit" className="inline-flex items-center justify-center h-11 w-11 rounded-full bg-nead-navy text-white hover:bg-nead-pink transition-colors disabled:opacity-50">
                <ArrowRight className="h-5 w-5" />
              </button>
            </form>
          </div>
        </div>

        <div className="grid gap-12 lg:grid-cols-[1.6fr_1fr_1fr_1.2fr]">
          <div>
            <Logo imgClassName="h-10" />
            <p className="mt-6 max-w-sm text-white/55 text-sm leading-relaxed">
              NEAD CIC, Northern Equality &amp; Diversity. Strengthening communities through
              programmes, festivals and youth provision, and delivering lasting social value across Cumbria.
            </p>
          </div>
          {cols.map((col) => (
            <div key={col.title}>
              <h4 className="text-xs font-semibold uppercase tracking-[0.16em] text-white/40 mb-5">{col.title}</h4>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}><Link to={l.to} className="text-white/70 text-sm hover:text-nead-pink transition-colors">{l.label}</Link></li>
                ))}
              </ul>
            </div>
          ))}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-[0.16em] text-white/40 mb-5">Get in touch</h4>
            <ul className="space-y-3 text-sm text-white/70">
              <li><a href="mailto:hello@nead.uk" className="hover:text-nead-pink transition-colors" data-testid="footer-email">hello@nead.uk</a></li>
              <li><a href="tel:+441946389606" className="hover:text-nead-pink transition-colors" data-testid="footer-phone">01946 389606</a></li>
              <li><a href="https://www.nead.uk" className="hover:text-nead-pink transition-colors" data-testid="footer-web">www.nead.uk</a></li>
              <li>Colours Diversity &amp; Inclusion Hub, Whitehaven, Cumbria</li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-white/10 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between text-xs text-white/40">
          <p>© {new Date().getFullYear()} NEAD CIC · Northern Equality &amp; Diversity.</p>
          <p>A Community Interest Company · Every surplus reinvested.</p>
        </div>
      </div>
    </footer>
  );
}
